export type UnitSystem = 'metric' | 'imperial';

export const KG_PER_LB = 0.45359237;
export const CM_PER_INCH = 2.54;

const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * 磅/盎司、厘米/英寸的输入框都是 precision={0}（只能输整数）。单位切换时如果换算结果
 * 带小数，会出现"输入框显示成整数、但表单里存的还是小数"的隐性不一致——不只是显示对不上，
 * 提交询价时用的也是那个看不见的小数，报价本身就已经和用户以为自己填的不一样了。
 * 所以这里所有"会写回输入框"的换算都四舍五入到整数，让"存的值"和"看到的值"永远相等。
 */
export const kgToLbOz = (kg?: number | null): { lb?: number; oz?: number } => {
  if (kg == null) return {};
  const totalLb = kg / KG_PER_LB;
  let lb = Math.floor(totalLb);
  let oz = Math.round((totalLb - lb) * 16);
  if (oz >= 16) {
    // 四舍五入可能刚好把盎司凑到 16，超过盎司输入框 max=15 的范围，要进 1 磅
    lb += 1;
    oz = 0;
  }
  return { lb, oz };
};

export const lbOzToKg = (lb?: number | null, oz?: number | null): number | undefined => {
  if (lb == null && oz == null) return undefined;
  const totalLb = (lb || 0) + (oz || 0) / 16;
  // 四舍五入到 3 位小数可能比精确值略大（比如整 3 磅换算出 1.36077711kg，四舍五入成
  // 1.361kg，比精确值多了约 0.0002kg）——真实调用 Ezeeship 验证过，多出来的这一点点会被
  // 承运商按"超出 3 磅、进到 4 磅档位"计费，3 磅和 4 磅报出完全相同（更贵）的价格。
  // 改成向下取整就不会有这个问题：算出来的公斤数只会比真实值小一点点，不会跨到更高的整磅档位。
  return Math.floor(totalLb * KG_PER_LB * 1000) / 1000;
};

/** 十进制公斤 -> 整数公斤 + 克余数，配合"M 公斤 N 克"两个输入框的样式 */
export const kgToKgGram = (kg?: number | null): { kg?: number; g?: number } => {
  if (kg == null) return {};
  let wholeKg = Math.floor(kg);
  let gram = Math.round((kg - wholeKg) * 1000);
  if (gram >= 1000) {
    // 同上：四舍五入可能刚好凑到 1000 克，超过克输入框 max=999 的范围，要进 1 公斤
    wholeKg += 1;
    gram = 0;
  }
  return { kg: wholeKg, g: gram };
};

export const kgGramToKg = (kg?: number | null, gram?: number | null): number | undefined => {
  if (kg == null && gram == null) return undefined;
  return round((kg || 0) + (gram || 0) / 1000, 3);
};

/** 写回厘米/英寸输入框用：四舍五入到整数，理由同上（这两个输入框也是 precision={0}） */
export const cmToInch = (cm?: number | null): number | undefined => {
  if (cm == null) return undefined;
  return Math.round(cm / CM_PER_INCH);
};

export const inchToCm = (inch?: number | null): number | undefined => {
  if (inch == null) return undefined;
  return Math.round(inch * CM_PER_INCH);
};

/**
 * 提交询价给后端用的精确换算（不写回任何输入框，不需要跟着某个输入框的 precision 取整）。
 * 英制输入的长宽高本身已经是整数英寸，换算成厘米时保留小数能让报价更准确，
 * 没必要像 cmToInch/inchToCm 那样为了迁就显示框而牺牲精度。
 */
const inchToCmPrecise = (inch?: number | null): number | undefined => {
  if (inch == null) return undefined;
  return round(inch * CM_PER_INCH, 3);
};

export interface PackageFormValue {
  /** 公制：整数公斤部分 */
  weight_kg?: number;
  /** 公制：克余数（0-999） */
  weight_g?: number;
  weight_lb?: number;
  weight_oz?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  length_in?: number;
  width_in?: number;
  height_in?: number;
  quantity?: number;
}

/** 单位开关切换时，把已经填写的数值从旧单位换算成新单位，避免用户切换后数据丢失 */
export const convertPackageUnits = (pkg: PackageFormValue, target: UnitSystem): PackageFormValue => {
  if (!pkg) return pkg;
  if (target === 'imperial') {
    const decimalKg = kgGramToKg(pkg.weight_kg, pkg.weight_g);
    const { lb, oz } = kgToLbOz(decimalKg);
    return {
      ...pkg,
      weight_lb: lb,
      weight_oz: oz,
      length_in: cmToInch(pkg.length_cm),
      width_in: cmToInch(pkg.width_cm),
      height_in: cmToInch(pkg.height_cm),
    };
  }
  const decimalKg = lbOzToKg(pkg.weight_lb, pkg.weight_oz);
  const { kg, g } = kgToKgGram(decimalKg);
  return {
    ...pkg,
    weight_kg: kg,
    weight_g: g,
    length_cm: inchToCm(pkg.length_in),
    width_cm: inchToCm(pkg.width_in),
    height_cm: inchToCm(pkg.height_in),
  };
};

/**
 * 提交询价前，把当前单位下的包裹数据统一换算成后端要求的公制字段（用精确换算，不取整）。
 * 公制分支下 length_cm/width_cm/height_cm 理论上输入框本身就只能填整数，这里仍然防御性地
 * 取整一次——万一表单里残留了旧版本换算逻辑写进去的小数（比如切换单位早于某次代码修复），
 * 也不会带着这种看不见的小数一路传到报价请求里。
 */
export interface WeightBreakdown {
  /** 主单位的整数部分：公制下是公斤，英制下是磅 */
  major: number;
  /** 副单位余数：公制下是克（0-999），英制下是盎司（0-15） */
  minor: number;
}

/**
 * 询价概览用：总重量直接在当前选择的单位下对所有包裹（含数量）做整数加总，
 * 全程不经过磅↔公斤的换算——磅/盎司、公斤/克两个子字段本身都是整数输入框，
 * 纯整数加减和进位不会有任何浮点/舍入误差。
 * 之前的实现是先统一转成公斤汇总、要显示磅的时候再除回去，那一步来回换算才是误差的根源。
 * 返回值是精确的"主单位+副单位"拆分，不做"凑整数显示"的进位——真实遇到过总重不足 1 磅
 * （比如 5 盎司）被强行显示成"1 磅"的问题：USPS Ground Advantage 这类按盎司计费的服务，
 * 5 盎司和 10 盎司报价完全不同，概览显示成同一个"1 磅"会让人看不出这个差异、误以为报价算错了。
 */
export const sumWeightNative = (packages: PackageFormValue[], unitSystem: UnitSystem): WeightBreakdown => {
  if (unitSystem === 'metric') {
    let totalKg = 0;
    let totalGram = 0;
    packages.forEach((pkg) => {
      const qty = Math.max(1, Math.round(pkg.quantity || 1));
      totalKg += (pkg.weight_kg || 0) * qty;
      totalGram += (pkg.weight_g || 0) * qty;
    });
    totalKg += Math.floor(totalGram / 1000);
    return { major: totalKg, minor: totalGram % 1000 };
  }
  let totalLb = 0;
  let totalOz = 0;
  packages.forEach((pkg) => {
    const qty = Math.max(1, Math.round(pkg.quantity || 1));
    totalLb += (pkg.weight_lb || 0) * qty;
    totalOz += (pkg.weight_oz || 0) * qty;
  });
  totalLb += Math.floor(totalOz / 16);
  return { major: totalLb, minor: totalOz % 16 };
};

/**
 * 把 sumWeightNative 的拆分结果格式化成展示文案：不足 1 个主单位时只显示副单位
 * （如"5 盎司"），凑整数（副单位为 0）时只显示主单位（如"3 磅"），否则两部分都显示
 * （如"1 磅 5 盎司"）。
 */
export const formatWeightBreakdown = (breakdown: WeightBreakdown, unitSystem: UnitSystem): string => {
  const majorUnit = unitSystem === 'metric' ? '公斤' : '磅';
  const minorUnit = unitSystem === 'metric' ? '克' : '盎司';
  if (breakdown.major === 0 && breakdown.minor === 0) return `0 ${majorUnit}`;
  if (breakdown.major === 0) return `${breakdown.minor} ${minorUnit}`;
  if (breakdown.minor === 0) return `${breakdown.major} ${majorUnit}`;
  return `${breakdown.major} ${majorUnit} ${breakdown.minor} ${minorUnit}`;
};

/**
 * 询价概览用：总体积同理，直接用当前单位下的长宽高整数输入框相乘再乘以数量，
 * 不经过厘米↔英寸的换算。整数 × 整数必然还是整数，天然没有小数、不需要额外取整。
 */
export const sumVolumeNative = (packages: PackageFormValue[], unitSystem: UnitSystem): number => {
  let total = 0;
  packages.forEach((pkg) => {
    const qty = Math.max(1, Math.round(pkg.quantity || 1));
    const l = unitSystem === 'metric' ? pkg.length_cm : pkg.length_in;
    const w = unitSystem === 'metric' ? pkg.width_cm : pkg.width_in;
    const h = unitSystem === 'metric' ? pkg.height_cm : pkg.height_in;
    total += (l || 0) * (w || 0) * (h || 0) * qty;
  });
  return total;
};

// 报价/购买相关页面展示金额一律带符号（如 $10），不是裸数字——目前所有物流渠道都是美元计价，
// 以后出现人民币等其他币种计价的产品时，符号会跟着 currency 字段自动切换，不用改代码。
// 没收录符号的币种（比如尚未支持的小语种货币代码）退化成"代码+空格"前缀，不瞎猜符号。
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  CNY: '¥',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  HKD: 'HK$',
  CAD: 'C$',
  AUD: 'A$',
};
export const formatMoney = (amount: number | string, currency: string): string => {
  const num = typeof amount === 'number' ? amount : Number(amount);
  const formatted = Number.isFinite(num) ? num.toFixed(2) : String(amount);
  const symbol = CURRENCY_SYMBOLS[currency];
  return symbol ? `${symbol}${formatted}` : `${currency} ${formatted}`;
};

export const packageToMetric = (pkg: PackageFormValue, unitSystem: UnitSystem) => {
  if (unitSystem === 'metric') {
    return {
      weight_kg: kgGramToKg(pkg.weight_kg, pkg.weight_g),
      length_cm: pkg.length_cm != null ? Math.round(pkg.length_cm) : pkg.length_cm,
      width_cm: pkg.width_cm != null ? Math.round(pkg.width_cm) : pkg.width_cm,
      height_cm: pkg.height_cm != null ? Math.round(pkg.height_cm) : pkg.height_cm,
    };
  }
  return {
    weight_kg: lbOzToKg(pkg.weight_lb, pkg.weight_oz),
    length_cm: inchToCmPrecise(pkg.length_in),
    width_cm: inchToCmPrecise(pkg.width_in),
    height_cm: inchToCmPrecise(pkg.height_in),
  };
};
